import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Compass, Download, Loader2, Mail, Search, Sparkles,
  Globe, ExternalLink, ChevronRight, CheckCircle2, ClipboardList, FileText,
} from "lucide-react";
import { toast } from "sonner";
import AiGeneratedBadge from "@/components/AiGeneratedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AXIS_LABEL = {
  DONNEES: "Donnees chiffrees",
  CONTEXTE: "Contexte & tendances",
  RISQUES: "Risques & alertes",
};

const AXIS_COLOR = {
  DONNEES: "#B5894A",
  CONTEXTE: "#1F6F6B",
  RISQUES: "#9C4A3C",
};

const CATEGORY_LABEL = {
  marche: "Analyse de marche",
  due_diligence: "Due diligence",
  fiscal_juridique: "Fiscalite / Juridique",
  veille: "Veille sectorielle",
};

const SAMPLE_QUERIES = [
  "Est-ce que le quartier Bacalan a Bordeaux est un bon investissement locatif en 2026 ?",
  "Quels sont les nouveaux dispositifs fiscaux LMNP en 2026 apres la reforme Le Meur ?",
  "Analyse le marche immobilier de Lille pour un investissement colocation etudiants",
  "Risques et opportunites d'investir dans une passoire thermique DPE F/G en 2026",
];

/** Retire les balises [Source: ...] et markdown links pour affichage propre */
function cleanContent(text) {
  if (!text) return "";
  return text
    .replace(/\[Source[^\]]*\]/g, "")
    .replace(/\s+([.,;])/g, "$1")
    .trim();
}

/** Rendu markdown-like minimal (paragraphs + bullets + numeric lists + gras) */
function renderMd(text) {
  if (!text) return null;
  const clean = cleanContent(text);
  const lines = clean.split("\n");
  const out = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      out.push(
        <p key={`p-${out.length}`} className="dr-paragraph">
          {renderInline(buf.join(" "))}
        </p>
      );
      buf = [];
    }
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (/^[-•*]\s+/.test(line)) {
      flush();
      out.push(
        <div key={`b-${out.length}`} className="dr-bullet">
          <span className="dr-bullet-dot">•</span>
          <span>{renderInline(line.replace(/^[-•*]\s+/, ""))}</span>
        </div>
      );
      return;
    }
    if (/^\d+[.)]\s+/.test(line)) {
      flush();
      const m = line.match(/^(\d+[.)])\s+(.*)$/);
      out.push(
        <div key={`n-${out.length}`} className="dr-bullet">
          <span className="dr-bullet-dot" style={{ color: "#B5894A", fontWeight: 700 }}>{m[1]}</span>
          <span>{renderInline(m[2])}</span>
        </div>
      );
      return;
    }
    // Section titles (all caps or known labels)
    const upper = line.replace(/:/g, "").trim().toUpperCase();
    const knownLabels = [
      "SYNTHESE EXECUTIVE", "DONNEES CLES", "OPPORTUNITES",
      "RISQUES ET POINTS DE VIGILANCE", "RECOMMANDATION",
      "PROCHAINES ETAPES", "SOURCES CONSULTEES", "CHIFFRES CLES",
    ];
    if (knownLabels.some((lbl) => upper.startsWith(lbl))) {
      flush();
      out.push(
        <h4 key={`h-${out.length}`} className="dr-section-title">
          {line.replace(/\*/g, "").trim()}
        </h4>
      );
      return;
    }
    buf.push(line);
  });
  flush();
  return out;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="dr-strong">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function DeepResearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [question, setQuestion] = useState("");
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | plan | research | synthesis | done
  const [researchId, setResearchId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [findings, setFindings] = useState([]); // [{index, axis, query, content, sources, streaming}]
  const [finalReport, setFinalReport] = useState("");
  const [finalStreaming, setFinalStreaming] = useState(false);
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const ctrlRef = useRef(null);
  const currentIndexRef = useRef(-1);
  const findingsRef = useRef([]);

  const resetState = () => {
    setPhase("idle");
    setResearchId(null);
    setPlan(null);
    setFindings([]);
    findingsRef.current = [];
    setFinalReport("");
    setFinalStreaming(false);
    currentIndexRef.current = -1;
  };

  const loadHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem("odys_token");
      const resp = await fetch(`${API}/researches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setHistory(data.researches || []);
    } catch (e) {
      console.warn("history load error", e);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const appendToken = (idx, text) => {
    findingsRef.current = findingsRef.current.map((f) =>
      f.index === idx ? { ...f, content: (f.content || "") + text } : f
    );
    setFindings([...findingsRef.current]);
  };

  const loadResearchFromDb = useCallback(async (rid) => {
    try {
      const token = localStorage.getItem("odys_token");
      const resp = await fetch(`${API}/research/${rid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setPlan(data.plan);
      const arr = (data.findings || []).map((f) => ({ ...f, streaming: false }));
      findingsRef.current = arr;
      setFindings(arr);
      if (data.final_report) {
        setFinalReport(data.final_report);
        setPhase("done");
      }
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const runSynthesis = useCallback(async (rid) => {
    setPhase("synthesis");
    setFinalStreaming(true);
    const token = localStorage.getItem("odys_token");
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    let acc = "";
    try {
      await fetchEventSource(`${API}/research/${rid}/synthesis/stream`, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        openWhenHidden: true,
        async onopen(response) {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        },
        onmessage(ev) {
          if (!ev.data) return;
          let parsed;
          try { parsed = JSON.parse(ev.data); } catch { return; }
          if (ev.event === "synthesis_token") {
            acc += parsed.text || "";
            setFinalReport(acc);
          } else if (ev.event === "synthesis_end") {
            const c = parsed.content || acc;
            acc = c;
            setFinalReport(c);
          } else if (ev.event === "done") {
            setPhase("done");
            setFinalStreaming(false);
            setRunning(false);
            loadHistory();
          } else if (ev.event === "error") {
            toast.error(parsed.message || "Erreur synthese");
          }
        },
        onerror(err) { throw err; },
      });
    } catch (err) {
      console.warn("synthesis SSE error, polling fallback", err);
      // Polling fallback
      const poll = async () => {
        const t = localStorage.getItem("odys_token");
        for (let i = 0; i < 60; i++) {
          await new Promise((r) => setTimeout(r, 2500));
          try {
            const resp = await fetch(`${API}/research/${rid}`, {
              headers: { Authorization: `Bearer ${t}` },
            });
            if (!resp.ok) continue;
            const data = await resp.json();
            if (data.final_report) {
              setFinalReport(data.final_report);
            }
            if (data.status === "done") {
              setPhase("done");
              setFinalStreaming(false);
              setRunning(false);
              loadHistory();
              return;
            }
          } catch (_e) { /* ignore poll error */ }
        }
        setFinalStreaming(false);
        setRunning(false);
      };
      poll();
    }
  }, [loadHistory]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (q.length < 10) {
      toast.error("Formule une question plus precise (10 caracteres minimum).");
      return;
    }
    setRunning(true);
    resetState();
    setPhase("plan");

    const token = localStorage.getItem("odys_token");
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    let rid = null;

    try {
      await fetchEventSource(`${API}/research/stream`, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ question: q }),
        openWhenHidden: true,
        async onopen(response) {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        },
        onmessage(ev) {
          if (!ev.data) return;
          let parsed;
          try { parsed = JSON.parse(ev.data); } catch { return; }

          if (ev.event === "plan_start") {
            rid = parsed.research_id;
            if (rid) setResearchId(rid);
          } else if (ev.event === "plan_ready") {
            setPlan(parsed.plan);
            setPhase("research");
            // Init findings placeholders
            const initial = (parsed.plan?.subqueries || []).map((sq, i) => ({
              index: i,
              axis: sq.axis,
              query: sq.query,
              content: "",
              sources: [],
              streaming: false,
              done: false,
            }));
            findingsRef.current = initial;
            setFindings(initial);
          } else if (ev.event === "subquery_start") {
            currentIndexRef.current = parsed.index;
            findingsRef.current = findingsRef.current.map((f) =>
              f.index === parsed.index ? { ...f, streaming: true } : f
            );
            setFindings([...findingsRef.current]);
          } else if (ev.event === "token") {
            const idx = parsed.index ?? currentIndexRef.current;
            if (idx >= 0) appendToken(idx, parsed.text || "");
          } else if (ev.event === "subquery_end") {
            findingsRef.current = findingsRef.current.map((f) =>
              f.index === parsed.index
                ? { ...f, streaming: false, done: true, content: parsed.content || f.content, sources: parsed.sources || [] }
                : f
            );
            setFindings([...findingsRef.current]);
          } else if (ev.event === "research_done") {
            // Ferme la phase 1, on lance la phase 2 dans une nouvelle requete (split proxy timeouts)
            if (rid) {
              try { ctrl.abort(); } catch (_e) { /* noop */ }
              runSynthesis(rid);
            }
          } else if (ev.event === "error") {
            toast.error(parsed.message || "Erreur pendant la recherche");
          }
        },
        onerror(err) { throw err; },
      });
    } catch (err) {
      console.warn("research SSE closed", err);
      // Si on a rid mais pas encore fini la synthese, on tente de recuperer via polling puis synthese
      if (rid && phase !== "synthesis" && phase !== "done") {
        // Poll findings then trigger synthesis
        setTimeout(async () => {
          await loadResearchFromDb(rid);
          runSynthesis(rid);
        }, 1000);
      } else if (!rid) {
        toast.error("Connexion perdue au demarrage. Reessaie.");
        setRunning(false);
        setPhase("idle");
      }
    }
  };

  const handleAbort = () => {
    try { ctrlRef.current?.abort(); } catch (_e) { /* noop */ }
    setRunning(false);
    setFinalStreaming(false);
    toast.info("Recherche interrompue. Le rapport en cours reste sauvegarde.");
  };

  const handleOpenHistory = async (rid) => {
    resetState();
    setResearchId(rid);
    await loadResearchFromDb(rid);
    setPhase("done");
  };

  const handleDeleteHistory = async (rid, e) => {
    e.stopPropagation();
    if (!confirm("Supprimer ce Deep Research ?")) return;
    try {
      const token = localStorage.getItem("odys_token");
      await fetch(`${API}/research/${rid}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      loadHistory();
      if (researchId === rid) resetState();
    } catch (e) {
      console.warn(e);
    }
  };

  const handleExportPdf = async () => {
    if (!researchId) return;
    setExporting(true);
    try {
      const token = localStorage.getItem("odys_token");
      const resp = await fetch(`${API}/research/${researchId}/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deep-research-odys-${researchId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF telecharge");
    } catch (e) {
      toast.error("Echec export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleEmailPdf = async () => {
    if (!researchId) return;
    setEmailing(true);
    try {
      const token = localStorage.getItem("odys_token");
      const resp = await fetch(`${API}/research/${researchId}/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Envoi echoue");
      toast.success(`PDF envoye a ${data.email}`);
    } catch (e) {
      toast.error(e.message || "Echec envoi email");
    } finally {
      setEmailing(false);
    }
  };

  const totalSubqueries = plan?.subqueries?.length || 0;
  const doneCount = findings.filter((f) => f.done).length;

  return (
    <div className="min-h-screen bg-[#F6F1E7] dark:bg-background text-[#16233B] dark:text-foreground">
      <style>{`
        .dr-paragraph { line-height: 1.65; margin: 6px 0; color: #16233B; }
        .dark .dr-paragraph { color: #E5E7EB; }
        .dr-bullet { display: flex; gap: 10px; margin: 4px 0; line-height: 1.55; }
        .dr-bullet-dot { color: #B5894A; font-weight: 700; flex-shrink: 0; margin-top: 2px; }
        .dr-strong { color: #16233B; font-weight: 700; }
        .dark .dr-strong { color: #F5E9CE; }
        .dr-section-title {
          font-family: var(--serif, 'Fraunces'), Georgia, serif;
          font-size: 14px; color: #B5894A; font-weight: 700;
          margin-top: 20px; margin-bottom: 6px;
          letter-spacing: 0.05em; text-transform: uppercase;
        }
        .dr-panel {
          background: linear-gradient(180deg, #FFFDF7 0%, #FBF6EC 100%);
          border: 1px solid #E9DCC4;
          box-shadow: 0 4px 24px -12px rgba(22, 35, 59, 0.15);
        }
        .dark .dr-panel {
          background: linear-gradient(180deg, #1E2536 0%, #16233B 100%);
          border-color: #B5894A;
        }
        .dr-gold-border-top { border-top: 3px solid #B5894A; }
        .dr-cta-primary {
          background: linear-gradient(145deg, #B5894A 0%, #8C6635 100%);
          color: #FAF7F1; font-weight: 600;
        }
        .dr-cta-primary:hover { filter: brightness(1.05); }
        .dr-shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(181, 137, 74, 0.15) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: dr-shim 1.8s linear infinite;
        }
        @keyframes dr-shim { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* Header */}
      <header className="border-b border-[#E3DCCD] dark:border-border bg-[#FFFDF7] dark:bg-background sticky top-0 z-10 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="dr-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <div className="min-w-0">
              <div className="text-[0.62rem] font-semibold uppercase tracking-[0.28em] text-[#B5894A]">
                Agent autonome
              </div>
              <h1
                className="text-lg md:text-xl font-bold truncate"
                style={{ fontFamily: "var(--serif, 'Fraunces')", color: "inherit" }}
              >
                Deep Research Immobilier
              </h1>
            </div>
          </div>
          <AiGeneratedBadge model="Claude Sonnet 4.6 + Opus 4.8" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
        {/* MAIN */}
        <main>
          {/* Composer */}
          {(phase === "idle" || phase === "done") && (
            <Card className="dr-panel dr-gold-border-top rounded-xl overflow-hidden mb-6">
              <CardContent className="p-5 md:p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: "linear-gradient(145deg, #B5894A 0%, #8C6635 100%)" }}
                  >
                    <Compass className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h2 className="font-bold text-base md:text-lg" style={{ fontFamily: "var(--serif, 'Fraunces')" }}>
                      Lance une recherche autonome
                    </h2>
                    <p className="text-sm text-[#3C4A63] dark:text-muted-foreground mt-1">
                      Un agent IA planifie 3 a 5 recherches web ciblees, lit les sources, et te livre un rapport structure. Compte 2 a 4 minutes.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <Textarea
                    data-testid="dr-question-input"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ex: Est-ce que Bordeaux Bacalan est un bon quartier pour un investissement LMNP en 2026 ?"
                    className="min-h-[100px] resize-none border-[#E3DCCD] dark:border-border focus-visible:ring-[#B5894A]"
                    disabled={running}
                  />
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      {SAMPLE_QUERIES.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setQuestion(s)}
                          disabled={running}
                          className="text-xs px-3 py-1.5 rounded-full border border-[#E3DCCD] dark:border-border bg-white/60 dark:bg-transparent hover:border-[#B5894A] hover:text-[#B5894A] transition-colors"
                          data-testid={`dr-sample-${i}`}
                        >
                          {s.length > 55 ? s.slice(0, 55) + "…" : s}
                        </button>
                      ))}
                    </div>
                    <Button
                      type="submit"
                      disabled={running || question.trim().length < 10}
                      className="dr-cta-primary rounded-lg px-5 h-11"
                      data-testid="dr-submit-btn"
                    >
                      {running ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recherche en cours…</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" /> Lancer la recherche</>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Progression */}
          {running && (
            <Card className="dr-panel rounded-xl mb-6">
              <CardContent className="p-4 md:p-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Sparkles className="w-4 h-4 text-[#B5894A]" />
                    {phase === "plan" && "Etape 1/3 : Planification…"}
                    {phase === "research" && `Etape 2/3 : Recherche web (${doneCount}/${totalSubqueries})`}
                    {phase === "synthesis" && "Etape 3/3 : Synthese finale…"}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAbort}
                    data-testid="dr-abort-btn"
                  >
                    Interrompre
                  </Button>
                </div>
                <div className="dr-shimmer h-1 rounded-full bg-[#F3EEE4] dark:bg-muted" />
              </CardContent>
            </Card>
          )}

          {/* PLAN */}
          {plan && (
            <Card className="dr-panel rounded-xl mb-5 overflow-hidden">
              <div className="dr-gold-border-top" />
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardList className="w-4 h-4 text-[#B5894A]" />
                  <div className="text-[0.62rem] font-semibold uppercase tracking-[0.28em] text-[#B5894A]">
                    Plan de recherche
                  </div>
                  {plan.category && (
                    <Badge variant="outline" className="ml-1 text-[10px] border-[#B5894A] text-[#B5894A]">
                      {CATEGORY_LABEL[plan.category] || plan.category}
                    </Badge>
                  )}
                </div>
                <h3
                  className="text-lg md:text-xl font-bold mb-3"
                  style={{ fontFamily: "var(--serif, 'Fraunces')" }}
                  data-testid="dr-plan-title"
                >
                  {plan.title || "Deep Research"}
                </h3>
                <ol className="space-y-2">
                  {(plan.subqueries || []).map((sq, i) => {
                    const done = findings.find((f) => f.index === i)?.done;
                    const streaming = findings.find((f) => f.index === i)?.streaming;
                    return (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span
                          className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                          style={{
                            background: done ? "#B5894A" : streaming ? "#F3EEE4" : "transparent",
                            color: done ? "#FAF7F1" : "#B5894A",
                            border: "1.5px solid #B5894A",
                          }}
                        >
                          {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: AXIS_COLOR[sq.axis] || "#B5894A" }}>
                            {AXIS_LABEL[sq.axis] || sq.axis}
                          </span>
                          <div className="text-[#16233B] dark:text-foreground">{sq.query}</div>
                        </div>
                        {streaming && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#B5894A]" />}
                      </li>
                    );
                  })}
                </ol>
              </CardContent>
            </Card>
          )}

          {/* FINDINGS */}
          {findings.map((f) => (
            <Card key={f.index} className="dr-panel rounded-xl mb-4 overflow-hidden" data-testid={`dr-finding-${f.index}`}>
              <div
                className="px-5 py-3 flex items-center gap-3"
                style={{
                  background: "linear-gradient(90deg, #FBF6EC 0%, #F3EEE4 100%)",
                  borderBottom: `2px solid ${AXIS_COLOR[f.axis] || "#B5894A"}`,
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: AXIS_COLOR[f.axis] || "#B5894A" }}
                >
                  <Globe className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#7A8194]">
                    Recherche {String(f.index + 1).padStart(2, "0")} — {AXIS_LABEL[f.axis] || f.axis}
                  </div>
                  <div
                    className="text-sm font-semibold truncate"
                    style={{ fontFamily: "var(--serif, 'Fraunces')" }}
                  >
                    {f.query}
                  </div>
                </div>
                {f.streaming && <Loader2 className="w-4 h-4 animate-spin text-[#B5894A]" />}
                {f.done && <CheckCircle2 className="w-4 h-4 text-[#1F6F6B]" />}
              </div>
              <CardContent className="p-5 text-sm">
                {f.content ? renderMd(f.content) : (
                  <div className="text-[#7A8194] italic">En attente…</div>
                )}
                {f.sources && f.sources.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-[#E3DCCD] dark:border-border">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#7A8194] mb-2 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Sources ({f.sources.length})
                    </div>
                    <ul className="space-y-1">
                      {f.sources.slice(0, 6).map((s, i) => (
                        <li key={i} className="text-xs flex items-start gap-2">
                          <span className="text-[#B5894A] mt-0.5">•</span>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1F6F6B] hover:underline flex-1 min-w-0 truncate"
                          >
                            {s.title || s.url}
                            <ExternalLink className="inline w-3 h-3 ml-1" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* SYNTHESIS / FINAL REPORT */}
          {(finalReport || phase === "synthesis") && (
            <Card className="dr-panel rounded-xl overflow-hidden mb-6" data-testid="dr-final-report">
              <div
                className="px-5 py-4 flex items-center justify-between gap-3"
                style={{
                  background: "linear-gradient(90deg, #16233B 0%, #1d2e4d 100%)",
                }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: "linear-gradient(145deg, #B5894A 0%, #8C6635 100%)" }}
                  >
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.28em]" style={{ color: "#E9DCC4" }}>
                      Rapport final Odys&apos;IA
                    </div>
                    <div
                      className="text-base font-bold truncate"
                      style={{ fontFamily: "var(--serif, 'Fraunces')", color: "#FAF7F1" }}
                    >
                      Synthese executive & recommandation
                    </div>
                  </div>
                </div>
                {finalStreaming && <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#B5894A" }} />}
              </div>
              <CardContent className="p-5 md:p-6 text-sm">
                {finalReport ? renderMd(finalReport) : (
                  <div className="text-[#7A8194] italic">Redaction du rapport final…</div>
                )}
              </CardContent>

              {phase === "done" && (
                <div className="px-5 py-4 border-t border-[#E3DCCD] dark:border-border flex flex-wrap gap-2 items-center bg-[#FBF6EC] dark:bg-transparent">
                  <Button
                    onClick={handleExportPdf}
                    disabled={exporting}
                    className="dr-cta-primary rounded-lg"
                    data-testid="dr-export-pdf-btn"
                  >
                    {exporting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Export…</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Telecharger le PDF</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleEmailPdf}
                    disabled={emailing}
                    className="border-[#B5894A] text-[#B5894A] hover:bg-[#FBF6EC]"
                    data-testid="dr-email-pdf-btn"
                  >
                    {emailing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> M&apos;envoyer par email</>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { resetState(); setQuestion(""); }}
                    data-testid="dr-new-btn"
                  >
                    Nouvelle recherche
                  </Button>
                </div>
              )}
            </Card>
          )}
        </main>

        {/* SIDEBAR HISTORY */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card className="dr-panel rounded-xl">
            <CardContent className="p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#B5894A] mb-3">
                Historique
              </div>
              {history.length === 0 ? (
                <div className="text-xs text-[#7A8194] italic py-2">
                  Tes recherches passees apparaitront ici.
                </div>
              ) : (
                <ul className="space-y-1.5" data-testid="dr-history-list">
                  {history.map((h) => (
                    <li key={h.research_id}>
                      <button
                        onClick={() => handleOpenHistory(h.research_id)}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-xs group transition-colors ${researchId === h.research_id ? "border-[#B5894A] bg-[#FBF6EC] dark:bg-[#16233B]" : "border-transparent hover:border-[#E9DCC4] hover:bg-[#FBF6EC]/60 dark:hover:bg-[#16233B]/40"}`}
                        data-testid={`dr-history-item-${h.research_id.slice(0, 8)}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate text-[#16233B] dark:text-foreground">
                              {h.plan?.title || h.question?.slice(0, 60) || "Deep Research"}
                            </div>
                            <div className="text-[10px] text-[#7A8194] flex items-center gap-1 mt-0.5">
                              {h.status === "done" ? (
                                <><CheckCircle2 className="w-3 h-3 text-[#1F6F6B]" /> Termine</>
                              ) : (
                                <><Loader2 className="w-3 h-3 animate-spin" /> En cours</>
                              )}
                              <span>· {h.created_at ? new Date(h.created_at).toLocaleDateString("fr-FR") : ""}</span>
                            </div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-[#B5894A] opacity-0 group-hover:opacity-100 shrink-0 mt-1" />
                        </div>
                        <span
                          onClick={(e) => handleDeleteHistory(h.research_id, e)}
                          className="text-[10px] text-[#7A8194] hover:text-red-500 mt-1 inline-block cursor-pointer"
                        >
                          Supprimer
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
